import { type ComponentProps, useCallback } from 'react';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeNavigation, useSafeRouter } from '@sg/garnish';

import { type OrderLineItem } from '@order/features/orders';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import type { ModalStackParamList } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const useNavigateToReorderCustomization = (params?: Params) => {
  const { shouldUseModalNavigation, shouldReplaceScreen = false } =
    params ?? {};

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const modalNavigation =
    useSafeNavigation<NativeStackNavigationProp<ModalStackParamList>>();
  const router = useSafeRouter();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToCustomizationScreen = useCallback<
    NonNullable<OrderLineItemProps['onLineItemCardPress']>
  >(
    (details) => {
      const { lineItemId, addressId, restaurantSlug, customName, product } =
        details;
      const productSlug = product?.slug ?? '';

      if (addressId) {
        if (isExpoRouterEnabled) {
          router?.navigate({
            pathname: '/delivery/[addressId]/[productSlug]',
            params: {
              addressId,
              productSlug,
              reorderLineItemId: lineItemId,
              ...(customName ? { name: customName } : {}),
            },
          });
          return;
        }

        navigation?.navigate('Modal', {
          screen: 'CustomizationDelivery',
          params: {
            reorderLineItemId: lineItemId,
            addressId,
            productSlug,
            ...(customName ? { name: customName } : {}),
          },
        });

        return;
      }

      if (isExpoRouterEnabled) {
        router?.navigate({
          pathname: '/[restaurantSlug]/[productSlug]',
          params: {
            restaurantSlug,
            productSlug,
            reorderLineItemId: lineItemId,
            ...(customName ? { name: customName } : {}),
          },
        });
        return;
      }

      navigation?.navigate('Modal', {
        screen: 'Customization',
        params: {
          reorderLineItemId: lineItemId,
          restaurantSlug,
          productSlug,
          ...(customName ? { name: customName } : {}),
        },
      });
    },
    [isExpoRouterEnabled, navigation, router],
  );

  const navigateToCustomizationModal = useCallback<
    NonNullable<OrderLineItemProps['onLineItemCardPress']>
  >(
    (details) => {
      const { lineItemId, addressId, restaurantSlug, customName, product } =
        details;
      const productSlug = product?.slug ?? '';

      if (addressId) {
        if (isExpoRouterEnabled) {
          const routeParams = {
            pathname: '/delivery/[addressId]/[productSlug]' as const,
            params: {
              addressId,
              productSlug,
              reorderLineItemId: lineItemId,
              ...(customName ? { name: customName } : {}),
            },
          };

          if (shouldReplaceScreen) {
            router?.replace(routeParams);
          } else {
            router?.navigate(routeParams);
          }

          return;
        }

        // NOTE: Reorder can be presented in a small modal on large breakpoints and a
        // fullscreen one on the smallest breakpoint which introduces some issues
        // We use different methods of navigation per breakpoint to avoid unexpected
        // issues.
        const navigationMethod = shouldReplaceScreen
          ? modalNavigation?.replace
          : modalNavigation?.navigate;

        navigationMethod?.('CustomizationDelivery', {
          reorderLineItemId: lineItemId,
          addressId,
          productSlug,
          ...(customName ? { name: customName } : {}),
        });

        return;
      }

      if (isExpoRouterEnabled) {
        const routeParams = {
          pathname: '/[restaurantSlug]/[productSlug]' as const,
          params: {
            restaurantSlug,
            productSlug,
            reorderLineItemId: lineItemId,
            ...(customName ? { name: customName } : {}),
          },
        };

        if (shouldReplaceScreen) {
          router?.replace(routeParams);
        } else {
          router?.navigate(routeParams);
        }

        return;
      }

      const navigationMethod = shouldReplaceScreen
        ? modalNavigation?.replace
        : modalNavigation?.navigate;

      navigationMethod?.('Customization', {
        reorderLineItemId: lineItemId,
        restaurantSlug,
        productSlug,
        ...(customName ? { name: customName } : {}),
      });
    },
    [isExpoRouterEnabled, modalNavigation, router, shouldReplaceScreen],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useCallback<NonNullable<OrderLineItemProps['onLineItemCardPress']>>(
    (details) => {
      if (shouldUseModalNavigation) {
        navigateToCustomizationModal(details);

        return;
      }

      navigateToCustomizationScreen(details);
    },
    [
      navigateToCustomizationModal,
      navigateToCustomizationScreen,
      shouldUseModalNavigation,
    ],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  shouldUseModalNavigation: boolean;
  shouldReplaceScreen?: boolean;
};

type OrderLineItemProps = ComponentProps<typeof OrderLineItem>;
